import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type {
  ApiClient, BrandschutzklappeZeile, BrandschutzklappenAnlagenmenge, BrandschutzklappenAnnahme,
  BrandschutzklappenAntwort, BrandschutzklappenMenge,
} from '../../api';
import type { Rahmen } from '../Projektrahmen';
import { PageHeader } from '../../rahmen/PageHeader';
import {
  Baum, ContentSection, DataTable, EmptyState, ErrorState, ExportLeiste, KpiCard, KpiReihe, LoadingState, OHNE_WERT,
  StatusBadge, Vorbehalte, formatiereZahl, type BaumKnoten, type ExportZiel, type Spalte,
} from '../../muster';
import { klappenbaum, klappenDerAuswahl, type Klappenknoten } from './brandschutzklappenbaum';
import './brandschutzklappen.css';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BRANDSCHUTZKLAPPEN (TV8, Eigentümerauftrag 20.10.2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * „eine brandschutzklappenliste mit aks nummern, anlagenzuordnung und
 * mengen … als eigenen menüpunkt … mit baumansicht und pdf export“.
 *
 * Der BESTAND aus dem Lüftungsmodell - dieselbe Erkennung wie die
 * Abgleichsliste der Prüfung (AKS-Kürzel BSK oder Baureihe). Lücken
 * (ohne AKS-Schlüssel, ohne Anlage, offene Katalogwerte) sind Befunde mit
 * Wort und Zahl; die Seite ergänzt nichts und schreibt nichts.
 *
 * Seite und Bericht LUE-BKL zeigen dasselbe Datenmodell: Kennzahlen,
 * Vorbehalte, Mengen und Zeilen kommen vom Server, nur der Baum wird hier
 * gegliedert (`brandschutzklappenbaum.ts`) - seine Zahlen sind Teilsummen
 * derselben Zeilen.
 */
export function LueftungBrandschutzklappen() {
  const { api, projekt } = useOutletContext<Rahmen>();
  // TV7a (GO main 20.10.): gegenseitiger Verweis mit dem Abgleich (TV5, Prüfung).
  const verweis = (
    <p className="seitenkopf-beschreibung" data-test="verweis-abgleich">
      Ob dieselbe Klappe in Lüftungsmodell und Gebäudeautomation gleich geführt ist, zeigt{' '}
      <Link to={`/projekte/${projekt.id}/lueftung/brandschutz`}>Brandschutzklappen und Entrauchung</Link> (Lüftung → Prüfung).
    </p>
  );
  return <BrandschutzklappenAnsicht api={api} projektId={projekt.id} verweis={verweis} />;
}

const LUECKE = 'Lücke';

/** Eine Lückenkennzahl: Grenze 0 - jede Klappe trägt den Wert. */
function Luecke({ titel, wert, gesamt }: { titel: string; wert: number; gesamt: number }) {
  return (
    <KpiCard
      titel={titel}
      wert={wert}
      vergleich={`von ${formatiereZahl(gesamt)} Klappen`}
      zustand={wert > 0 ? 'warnung' : 'gut'}
      zustandText={wert > 0 ? LUECKE : 'vollständig'}
      fussnote="Soll: 0 - jede Klappe trägt den Wert"
    />
  );
}

function leer(w: string | null): string {
  return w ?? OHNE_WERT;
}

const KLAPPEN_SPALTEN: Spalte<BrandschutzklappeZeile & { nr: number }>[] = [
  {
    schluessel: 'aks', titel: 'AKS-Schlüssel', immerSichtbar: true,
    wert: (z) => z.aksSchluessel ?? '',
    inhalt: (z) => (
      <>
        {z.aksSchluessel ?? <StatusBadge zustand="warnung" text="ohne AKS-Schlüssel" klein />}
        {z.luecken.includes('aks_abweichend') && (
          <> <StatusBadge zustand="warnung" text={`Kürzel ${z.aksKuerzel ?? ''} weicht ab`} klein /></>
        )}
      </>
    ),
  },
  {
    schluessel: 'anlage', titel: 'Anlage', wert: (z) => z.anlage,
    inhalt: (z) => (z.hatAnlage ? z.anlage : <StatusBadge zustand="warnung" text="ohne Anlage" klein />),
  },
  { schluessel: 'ebene', titel: 'Ebene', wert: (z) => z.ebene },
  { schluessel: 'raum', titel: 'Raum', wert: (z) => z.raum ?? '', inhalt: (z) => leer(z.raum) },
  { schluessel: 'baureihe', titel: 'Baureihe', wert: (z) => z.baureihe ?? '', inhalt: (z) => leer(z.baureihe) },
  { schluessel: 'typ', titel: 'Typ', wert: (z) => z.typ ?? '', inhalt: (z) => leer(z.typ) },
  { schluessel: 'groesse', titel: 'Größe', wert: (z) => z.groesse ?? '', inhalt: (z) => leer(z.groesse) },
  { schluessel: 'ausloesung', titel: 'Auslösung', wert: (z) => z.ausloesung ?? '', inhalt: (z) => leer(z.ausloesung) },
  {
    schluessel: 'feuerwiderstand', titel: 'Feuerwiderstand', wert: (z) => z.feuerwiderstand ?? '',
    inhalt: (z) => leer(z.feuerwiderstand),
  },
  {
    schluessel: 'brandabschnitt', titel: 'Brandabschnitt', wert: (z) => z.brandabschnitt ?? '',
    inhalt: (z) => leer(z.brandabschnitt),
  },
  {
    schluessel: 'herkunft', titel: 'Herkunft', wert: (z) => z.herkunft,
    inhalt: (z) => (z.erkennung === 'baureihe'
      ? <StatusBadge zustand="info" text={z.herkunft} klein />
      : z.herkunft),
  },
  { schluessel: 'revitId', titel: 'Revit-ID', wert: (z) => z.revitId ?? '', versteckt: true },
];

const MENGEN_SPALTEN: Spalte<BrandschutzklappenMenge>[] = [
  { schluessel: 'baureihe', titel: 'Baureihe' },
  { schluessel: 'groesse', titel: 'Größe' },
  { schluessel: 'klappen', titel: 'Klappen', art: 'zahl', fuss: 'summe' },
  { schluessel: 'ohneAksSchluessel', titel: 'davon ohne AKS-Schlüssel', art: 'zahl', fuss: 'summe' },
];

const ANLAGEN_SPALTEN: Spalte<BrandschutzklappenAnlagenmenge>[] = [
  {
    schluessel: 'anlage', titel: 'Anlage',
    inhalt: (z) => (z.hatAnlage ? z.anlage : <StatusBadge zustand="warnung" text="ohne Anlage" klein />),
  },
  { schluessel: 'klappen', titel: 'Klappen', art: 'zahl', fuss: 'summe' },
  { schluessel: 'ueberAks', titel: 'über AKS', art: 'zahl', fuss: 'summe' },
  { schluessel: 'ueberBaureihe', titel: 'über Baureihe', art: 'zahl', fuss: 'summe' },
  { schluessel: 'ohneAksSchluessel', titel: 'ohne AKS-Schlüssel', art: 'zahl', fuss: 'summe' },
  { schluessel: 'ebenen', titel: 'Ebenen', art: 'zahl' },
];

const ANNAHMEN_SPALTEN: Spalte<BrandschutzklappenAnnahme>[] = [
  { schluessel: 'parameter', titel: 'Parameter' },
  { schluessel: 'wert', titel: 'Wert', inhalt: (z) => leer(z.wert) },
  { schluessel: 'herkunft', titel: 'Herkunft' },
  { schluessel: 'quelle', titel: 'Quelle', inhalt: (z) => leer(z.quelle) },
  {
    schluessel: 'bestaetigt', titel: 'Bestätigt', art: 'status',
    inhalt: (z) => (z.bestaetigt
      ? <StatusBadge zustand="gut" text="bestätigt" klein />
      : <StatusBadge zustand="warnung" text="unbestätigt" klein />),
  },
];

/**
 * Das Datum der Aufnahme so, wie es im Bericht steht: der Kalendertag des gespeicherten Zeitpunkts, ohne Umrechnung
 * in die Zeitzone des Browsers (sonst stünde nach 22 Uhr UTC hier ein anderer Tag als auf dem Blatt).
 */
export function aufnahmetag(iso: string | null): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  return m ? `${m[3]}.${m[2]}.${m[1]}` : null;
}

/** Die Seite ohne Rahmen - prüfbar mit einer Attrappe der Schnittstelle. */
export function BrandschutzklappenAnsicht({ api, projektId, verweis }: {
  api: ApiClient; projektId: string;
  /** Verweis unter dem Kopf (Router nötig) - die Seite gibt ihn mit, die Ansicht allein braucht keinen Router. */
  verweis?: ReactNode;
}) {
  const [daten, setDaten] = useState<BrandschutzklappenAntwort | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [laedt, setLaedt] = useState(true);
  const [gewaehlt, setGewaehlt] = useState<BaumKnoten<Klappenknoten> | null>(null);

  const laden = useCallback(() => {
    setLaedt(true);
    setFehler(null);
    api.lueftungBrandschutzklappen(projektId)
      .then(setDaten)
      .catch(setFehler)
      .finally(() => setLaedt(false));
  }, [api, projektId]);

  useEffect(() => { laden(); }, [laden]);

  const modell = daten?.modell ?? null;
  const zeilen = useMemo(() => (modell?.zeilen ?? []).map((z, nr) => ({ ...z, nr })), [modell]);
  const baum = useMemo(() => klappenbaum(modell?.zeilen ?? []), [modell]);
  const auswahl = gewaehlt?.daten?.auswahl ?? null;
  const gezeigt = useMemo(() => klappenDerAuswahl(zeilen, auswahl), [zeilen, auswahl]);

  const ziele: ExportZiel[] = [
    { art: 'pdf', beschriftung: 'PDF gesamt', laden: () => api.lueftungBrandschutzklappenPdf(projektId) },
    {
      art: 'pdf',
      beschriftung: auswahl ? `PDF ${auswahl.titel}` : 'PDF Auswahl',
      laden: () => api.lueftungBrandschutzklappenPdf(projektId, auswahl?.anlage, auswahl?.ebene),
      gesperrt: auswahl ? undefined : { grund: 'Im Baum eine Anlage oder Ebene wählen.' },
    },
  ];
  const ohneKlappen = !modell || modell.klappen === 0;
  const stand = aufnahmetag(daten?.modellstand ?? null);

  return (
    <>
      <PageHeader
        titel="Brandschutzklappen"
        beschreibung="Bestand der Brandschutzklappen aus dem Lüftungsmodell mit AKS-Schlüssel, Anlage, Ebene und Mengen. Lücken sind Befunde - die Liste ergänzt nichts."
        meta={stand ? `Lüftungsaufnahme vom ${stand}${daten?.status ? ` · ${daten.status}` : ''}` : undefined}
        aktionen={ohneKlappen ? undefined : <ExportLeiste ziele={ziele} />}
      >
        {verweis}
      </PageHeader>

      {laedt && <LoadingState text="Die Brandschutzklappen werden abgerufen …" zeilen={8} spalten={6} />}

      {!laedt && fehler !== null && (
        <ErrorState meldung="Die Brandschutzklappen konnten nicht abgerufen werden." technisch={fehler}
          beiErneutVersuchen={laden} />
      )}

      {!laedt && fehler === null && daten?.hinweis && (
        <EmptyState titel="Keine Erkennung möglich." erklaerung={daten.hinweis} />
      )}

      {!laedt && fehler === null && daten && !daten.hinweis && ohneKlappen && (
        <EmptyState titel="Keine Brandschutzklappe erkannt."
          erklaerung="Im Lüftungsmodell trägt kein Bauteil das AKS-Kürzel BSK oder eine Baureihe der Regel bsk_liste." />
      )}

      {!laedt && fehler === null && modell && !ohneKlappen && (
        <>
          <Vorbehalte eintraege={(daten?.vorbehalte ?? []).map((v) => ({ art: v.art, text: v.text, verweis: v.verweis ?? undefined }))} />

          <KpiReihe>
            <KpiCard
              titel="Brandschutzklappen"
              wert={modell.klappen}
              vergleich={`${formatiereZahl(modell.ueberAks)} über AKS · ${formatiereZahl(modell.ueberBaureihe)} über Baureihe · ${formatiereZahl(modell.anlagen)} Anlagen`}
            />
            <Luecke titel="ohne AKS-Schlüssel" wert={modell.luecken.ohneAksSchluessel} gesamt={modell.klappen} />
            <Luecke titel="ohne Anlage" wert={modell.luecken.ohneAnlage} gesamt={modell.klappen} />
            <Luecke titel="Auslösung offen" wert={modell.luecken.ausloesungOffen} gesamt={modell.klappen} />
            <Luecke titel="Feuerwiderstand offen" wert={modell.luecken.feuerwiderstandOffen} gesamt={modell.klappen} />
            <Luecke titel="Brandabschnitt offen" wert={modell.luecken.brandabschnittOffen} gesamt={modell.klappen} />
          </KpiReihe>

          <ContentSection
            titel="Gliederung nach Anlage und Ebene"
            beschreibung="Links eine Anlage oder Ebene wählen, rechts stehen ihre Klappen. Die Zahl am Knoten ist die Summe seiner Ebenen."
            aktionen={gewaehlt
              ? <button type="button" className="m-knopf" onClick={() => setGewaehlt(null)}>Auswahl aufheben</button>
              : undefined}
          >
            <div className="lue-bkl-raster">
              <Baum
                name="Brandschutzklappen nach Anlage und Ebene"
                knoten={baum}
                gewaehlt={gewaehlt?.id ?? null}
                beiWahl={(k) => setGewaehlt(k)}
              />
              <DataTable
                name={auswahl ? auswahl.titel : 'Alle Klappen'}
                spalten={KLAPPEN_SPALTEN}
                zeilen={gezeigt}
                schluessel={(z) => String(z.nr)}
                ausfuhr
                ausfuhrDateiname="brandschutzklappen.csv"
                suchePlatzhalter="AKS-Schlüssel, Raum, Typ …"
              />
            </div>
          </ContentSection>

          <ContentSection titel="Mengen je Baureihe und Größe">
            <DataTable spalten={MENGEN_SPALTEN} zeilen={[...modell.jeBaureihe]}
              schluessel={(z) => `${z.baureihe}|${z.groesse}`} suche={false} />
          </ContentSection>

          <ContentSection titel="Mengen je Anlage">
            <DataTable spalten={ANLAGEN_SPALTEN} zeilen={[...modell.jeAnlage]}
              schluessel={(z) => `${z.hatAnlage ? 'a' : '-'}|${z.anlage}`} suche={false} />
          </ContentSection>

          <ContentSection titel="Annahmen und Vorgaben">
            <DataTable spalten={ANNAHMEN_SPALTEN} zeilen={[...modell.annahmen]}
              schluessel={(z) => z.parameter} suche={false} spaltenwahl={false} />
          </ContentSection>
        </>
      )}
    </>
  );
}
